import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Stat Schemas - Zod definitions for stat types
 * These provide runtime validation and TypeScript type inference
 */

// Base stat fields shared by all sport types
const baseStatFields = {
  _id: objectId,
  player: objectId,
  division: objectId,
  league: objectId,
  organization: objectId,
  game: objectId,
  team: objectId,
  vsTeam: objectId,
  ...timestampFields,
};

// Metadata for base stat fields (for Mongoose refs and UI)
export const baseStatMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  player: { ref: 'Player', label: 'Player' },
  division: { ref: 'Division', hidden: true },
  league: { ref: 'League', hidden: true },
  organization: { ref: 'Organization', hidden: true },
  game: { ref: 'Game', hidden: true },
  team: { ref: 'DivisionTeam', label: 'Team' },
  vsTeam: { ref: 'DivisionTeam', label: 'Vs Team' },
  created: { hidden: true },
  updated: { hidden: true },
};

// Basketball Stats
export const BasketballStatSchema = z.object({
  ...baseStatFields,
  points: z.number().default(-1),
  assists: z.number().default(-1),
  offensiveRebounds: z.number().default(-1),
  defensiveRebounds: z.number().default(-1),
  totalRebounds: z.number().default(-1),
  steals: z.number().default(-1),
  blocks: z.number().default(-1),
  fieldGoalsAttempted: z.number().default(-1),
  fieldGoalsMade: z.number().default(-1),
  threePointsAttempted: z.number().default(-1),
  threePointsMade: z.number().default(-1),
  freeThrowsAttempted: z.number().default(-1),
  freeThrowsMade: z.number().default(-1),
  fouls: z.number().default(-1),
  turnovers: z.number().default(-1),
});

// `agg` is the per-game-average key derived from each raw stat (used to infer the
// public aggregate columns from the gameStats whitelist). All follow `avg`+Capitalize
// EXCEPT totalRebounds → avgRebounds (matches statParser.playerCummulativeStats).
export const basketballStatMetadata: Record<string, FieldMetadata> = {
  ...baseStatMetadata,
  points: { label: 'Points', headerName: 'PTS', editable: true, width: 80, agg: 'avgPoints' },
  assists: { label: 'Assists', headerName: 'AST', editable: true, width: 80, agg: 'avgAssists' },
  offensiveRebounds: { label: 'Offensive Rebounds', headerName: 'OREB', editable: true, width: 80 },
  defensiveRebounds: { label: 'Defensive Rebounds', headerName: 'DREB', editable: true, width: 80 },
  totalRebounds: { label: 'Total Rebounds', headerName: 'REB', editable: true, width: 80, agg: 'avgRebounds' },
  steals: { label: 'Steals', headerName: 'STL', editable: true, width: 80, agg: 'avgSteals' },
  blocks: { label: 'Blocks', headerName: 'BLK', editable: true, width: 80, agg: 'avgBlocks' },
  fieldGoalsAttempted: { label: 'FG Attempted', headerName: 'FGA', editable: true, width: 80, agg: 'avgFieldGoalsAttempted' },
  fieldGoalsMade: { label: 'FG Made', headerName: 'FGM', editable: true, width: 80, agg: 'avgFieldGoalsMade' },
  threePointsAttempted: { label: '3PT Attempted', headerName: '3PA', editable: true, width: 80, agg: 'avgThreePointsAttempted' },
  threePointsMade: { label: '3PT Made', headerName: '3PM', editable: true, width: 80, agg: 'avgThreePointsMade' },
  freeThrowsAttempted: { label: 'FT Attempted', headerName: 'FTA', editable: true, width: 80, agg: 'avgFreeThrowsAttempted' },
  freeThrowsMade: { label: 'FT Made', headerName: 'FTM', editable: true, width: 80, agg: 'avgFreeThrowsMade' },
  fouls: { label: 'Fouls', headerName: 'PF', editable: true, width: 80, agg: 'avgFouls' },
  turnovers: { label: 'Turnovers', headerName: 'TO', editable: true, width: 80 },
};

// Volleyball Stats — `-1` = "not entered" sentinel, matching basketball.
export const VolleyballStatSchema = z.object({
  ...baseStatFields,
  kills: z.number().default(-1),
  attackErrors: z.number().default(-1),
  attackAttempts: z.number().default(-1),
  assists: z.number().default(-1),
  serviceAces: z.number().default(-1),
  serviceErrors: z.number().default(-1),
  digs: z.number().default(-1),
  blockSolos: z.number().default(-1),
  blockAssists: z.number().default(-1),
  receptionErrors: z.number().default(-1),
});

export const volleyballStatMetadata: Record<string, FieldMetadata> = {
  ...baseStatMetadata,
  kills: { label: 'Kills', headerName: 'K', editable: true, width: 80, agg: 'avgKills' },
  attackErrors: { label: 'Attack Errors', headerName: 'AE', editable: true, width: 80 },
  attackAttempts: { label: 'Attack Attempts', headerName: 'ATT', editable: true, width: 80 },
  assists: { label: 'Assists', headerName: 'AST', editable: true, width: 80, agg: 'avgAssists' },
  serviceAces: { label: 'Service Aces', headerName: 'SA', editable: true, width: 80, agg: 'avgServiceAces' },
  serviceErrors: { label: 'Service Errors', headerName: 'SE', editable: true, width: 80 },
  digs: { label: 'Digs', headerName: 'D', editable: true, width: 80, agg: 'avgDigs' },
  blockSolos: { label: 'Block Solos', headerName: 'BS', editable: true, width: 80, agg: 'avgBlockSolos' },
  blockAssists: { label: 'Block Assists', headerName: 'BA', editable: true, width: 80 },
  receptionErrors: { label: 'Reception Errors', headerName: 'RE', editable: true, width: 80 },
};

// Soccer Stats
export const SoccerStatSchema = z.object({
  ...baseStatFields,
  goals: z.number().default(-1),
  assists: z.number().default(-1),
  shots: z.number().default(-1),
  shotsOnTarget: z.number().default(-1),
  saves: z.number().default(-1),
  yellowCards: z.number().default(-1),
  redCards: z.number().default(-1),
});

export const soccerStatMetadata: Record<string, FieldMetadata> = {
  ...baseStatMetadata,
  goals: { label: 'Goals', headerName: 'G', editable: true, width: 80, agg: 'avgGoals' },
  assists: { label: 'Assists', headerName: 'A', editable: true, width: 80, agg: 'avgAssists' },
  shots: { label: 'Shots', headerName: 'SH', editable: true, width: 80 },
  shotsOnTarget: { label: 'Shots on Target', headerName: 'SOT', editable: true, width: 80, agg: 'avgShotsOnTarget' },
  saves: { label: 'Saves', headerName: 'SV', editable: true, width: 80, agg: 'avgSaves' },
  yellowCards: { label: 'Yellow Cards', headerName: 'YC', editable: true, width: 80 },
  redCards: { label: 'Red Cards', headerName: 'RC', editable: true, width: 80 },
};

// Softball Stats (INT-0025) — the standard diamond batting line. NOTE:
// baseball has NO stat schema (its games live in a separate score-only
// collection); softball is deliberately first-class on the
// basketball/volleyball/soccer pattern instead of aliasing baseball.
export const SoftballStatSchema = z.object({
  ...baseStatFields,
  runs: z.number().default(-1),
  hits: z.number().default(-1),
  doubles: z.number().default(-1),
  triples: z.number().default(-1),
  homeRuns: z.number().default(-1),
  rbis: z.number().default(-1),
  walks: z.number().default(-1),
  strikeouts: z.number().default(-1),
  stolenBases: z.number().default(-1),
});

export const softballStatMetadata: Record<string, FieldMetadata> = {
  ...baseStatMetadata,
  runs: { label: 'Runs', headerName: 'R', editable: true, width: 80, agg: 'avgRuns' },
  hits: { label: 'Hits', headerName: 'H', editable: true, width: 80, agg: 'avgHits' },
  doubles: { label: 'Doubles', headerName: '2B', editable: true, width: 80 },
  triples: { label: 'Triples', headerName: '3B', editable: true, width: 80 },
  homeRuns: { label: 'Home Runs', headerName: 'HR', editable: true, width: 80, agg: 'avgHomeRuns' },
  rbis: { label: 'RBIs', headerName: 'RBI', editable: true, width: 80, agg: 'avgRbis' },
  walks: { label: 'Walks', headerName: 'BB', editable: true, width: 80 },
  strikeouts: { label: 'Strikeouts', headerName: 'SO', editable: true, width: 80 },
  stolenBases: { label: 'Stolen Bases', headerName: 'SB', editable: true, width: 80, agg: 'avgStolenBases' },
};

// Baseball Stats (INT-0028, owner 2026-09-16) — identical batting line to
// softball, as its OWN first-class schema per the one-entry-per-sport
// pattern (the old "baseball games live in a separate score-only
// collection" era ends here; INT-0025's softball note is superseded).
export const BaseballStatSchema = z.object({
  ...baseStatFields,
  runs: z.number().default(-1),
  hits: z.number().default(-1),
  doubles: z.number().default(-1),
  triples: z.number().default(-1),
  homeRuns: z.number().default(-1),
  rbis: z.number().default(-1),
  walks: z.number().default(-1),
  strikeouts: z.number().default(-1),
  stolenBases: z.number().default(-1),
});

export const baseballStatMetadata: Record<string, FieldMetadata> = {
  ...baseStatMetadata,
  runs: { label: 'Runs', headerName: 'R', editable: true, width: 80, agg: 'avgRuns' },
  hits: { label: 'Hits', headerName: 'H', editable: true, width: 80, agg: 'avgHits' },
  doubles: { label: 'Doubles', headerName: '2B', editable: true, width: 80 },
  triples: { label: 'Triples', headerName: '3B', editable: true, width: 80 },
  homeRuns: { label: 'Home Runs', headerName: 'HR', editable: true, width: 80, agg: 'avgHomeRuns' },
  rbis: { label: 'RBIs', headerName: 'RBI', editable: true, width: 80, agg: 'avgRbis' },
  walks: { label: 'Walks', headerName: 'BB', editable: true, width: 80 },
  strikeouts: { label: 'Strikeouts', headerName: 'SO', editable: true, width: 80 },
  stolenBases: { label: 'Stolen Bases', headerName: 'SB', editable: true, width: 80, agg: 'avgStolenBases' },
};

// Sport → stat metadata, for sport-aware derivation of aggregate columns.
export const STAT_METADATA_BY_SPORT: Record<string, Record<string, FieldMetadata>> = {
  basketball: basketballStatMetadata,
  volleyball: volleyballStatMetadata,
  soccer: soccerStatMetadata,
  softball: softballStatMetadata,
  baseball: baseballStatMetadata,
};

// Selectable raw stat keys per sport (the catalog shown in the Settings picker /
// stat-entry grid). Single source for STAT_FIELDS across admin + consumers.
export const STAT_FIELDS_BY_SPORT: Record<string, string[]> = {
  basketball: [
    'points', 'assists', 'offensiveRebounds', 'defensiveRebounds', 'totalRebounds',
    'steals', 'blocks', 'fieldGoalsAttempted', 'fieldGoalsMade', 'threePointsAttempted',
    'threePointsMade', 'freeThrowsAttempted', 'freeThrowsMade', 'fouls', 'turnovers',
  ],
  volleyball: [
    'kills', 'attackErrors', 'attackAttempts', 'assists', 'serviceAces',
    'serviceErrors', 'digs', 'blockSolos', 'blockAssists', 'receptionErrors',
  ],
  soccer: [
    'goals', 'assists', 'shots', 'shotsOnTarget', 'saves', 'yellowCards', 'redCards',
  ],
  softball: [
    'runs', 'hits', 'doubles', 'triples', 'homeRuns', 'rbis', 'walks',
    'strikeouts', 'stolenBases',
  ],
  baseball: [
    'runs', 'hits', 'doubles', 'triples', 'homeRuns', 'rbis', 'walks',
    'strikeouts', 'stolenBases',
  ],
};

// Default enabled gameStats per sport (used when config.gameStats is unset).
export const DEFAULT_GAME_STATS_BY_SPORT: Record<string, string[]> = {
  basketball: ['points', 'threePointsMade', 'totalRebounds', 'assists', 'steals', 'blocks'],
  volleyball: ['kills', 'serviceAces', 'digs', 'blockSolos', 'assists'],
  soccer: ['goals', 'assists', 'shotsOnTarget', 'saves', 'yellowCards'],
  softball: ['runs', 'hits', 'homeRuns', 'rbis'],
  baseball: ['runs', 'hits', 'homeRuns', 'rbis'],
};

export type BasketballStat = z.infer<typeof BasketballStatSchema>;
export type VolleyballStat = z.infer<typeof VolleyballStatSchema>;
export type SoccerStat = z.infer<typeof SoccerStatSchema>;
export type SoftballStat = z.infer<typeof SoftballStatSchema>;
export type BaseballStat = z.infer<typeof BaseballStatSchema>;

export type Stat = BasketballStat | VolleyballStat | SoccerStat | SoftballStat | BaseballStat;
